import { useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import {
  AddInstantBillV2Activity,
  AddInstantBillV2FormActivityFields,
  PaymentFlowDoneAction,
  RecurringPaymentFlowActivity,
} from '@melio/ap-activities';
import { isBillRecurringFrequency } from '@melio/ap-domain';
import { RecurringEndDateFields, RecurringNumOfOccurrencesFields } from '@melio/ap-widgets';
import { useToast } from '@melio/penny';
import { OriginFlow } from '@melio/platform-analytics';
import { BillSubscription } from '@melio/platform-api';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { useMelioIntl } from '@melio/platform-i18n';
import { useNavigate } from '@melio/platform-utils';

import { AddNewBillStateType, useRouter } from '@/hooks/router.hooks';
import { useAddInstantBillRedirects } from '@/hooks/useAddInstantBillRedirects.hooks';
import { PayDashboardTabs } from '@/types/payDashboard.types';

type Props = {
  defaultValues?: Partial<AddInstantBillV2FormActivityFields>;
};

export const AddInstantBillV2Screen = ({ defaultValues }: Props) => {
  const [addBillFormState, setAddBillFormState] = useState<AddInstantBillV2FormActivityFields>();
  const [flowUuid, setFlowUuid] = useState<string>('');
  const { state } = useLocation();
  const { formatMessage } = useMelioIntl();
  const toast = useToast();
  const { replaceToPayDashboardTab, generateNPEDashboardLink, goToAddNewBill } = useRouter();
  const navigate = useNavigate();
  const { returnUrl } = (state as { returnUrl?: string }) || {};
  const { onDone, onClose } = useAddInstantBillRedirects(returnUrl);
  const [isRecurringPaymentsEnabled] = useFeature<boolean>(FeatureFlags.RecurringPayments, false);

  const [isInternationalSupportedForRecurringFlow] = useFeature(
    FeatureFlags.IsInternationalSupportedForRecurringFlow,
    false,
  );
  const [isPlatformJustPayV2RecurringEnabled] = useFeature(FeatureFlags.JustPayV2Recurring, false);

  useEffect(() => {
    setFlowUuid(`${new Date().valueOf()}${Math.floor(Math.random() * 10000000)}`);
  }, [setFlowUuid]);

  const onDoneAddInstantBill = ({ data, billId }: { data?: AddInstantBillV2FormActivityFields; billId?: string }) => {
    if (isBillRecurringFrequency(data?.paymentFrequency || '')) {
      setAddBillFormState(data);
      navigate('recurring');
    } else {
      onDone(billId);
    }
  };

  const onFlowDone = (billSubscription: BillSubscription, action: PaymentFlowDoneAction = 'goToDashboard') => {
    if (action === 'goToDashboard') {
      if (returnUrl) {
        const isSingleOccurrence = billSubscription.numOfOccurrences == null || billSubscription.numOfOccurrences === 1;
        const toastTitle = isSingleOccurrence
          ? formatMessage('screens.addBillAndSchedulePayment.toast.success.single.title')
          : formatMessage('screens.addBillAndSchedulePayment.toast.success.multiple.title', {
              numOfOccurrences: billSubscription.numOfOccurrences,
            });
        const toastActionText = formatMessage('screens.batchPayments.toast.success.action', {
          pluralSuffix: isSingleOccurrence ? '' : 's',
        });
        toast({
          type: 'success',
          title: toastTitle,
          action: {
            type: 'button',
            text: toastActionText,
            onAction: (closeToast) => {
              navigate(generateNPEDashboardLink('payments'));
              closeToast();
            },
          },
        });

        return navigate(returnUrl);
      }
      handleGoToDashboard(billSubscription);
    }
  };
  const handleGoToDashboard = (billSubscription: BillSubscription) => {
    const paymentId = billSubscription?.occurrences?.[0]?.paymentId;
    replaceToPayDashboardTab(PayDashboardTabs.Scheduled, paymentId || undefined);
  };

  return (
    <Routes>
      <Route
        index
        element={
          <AddInstantBillV2Activity
            onClose={onClose}
            onGoToDashboard={onClose}
            onGoToAddNewBill={(props) =>
              goToAddNewBill({
                ...props,
                originFlow: OriginFlow.JustPay,
              } as AddNewBillStateType)
            }
            onDone={onDoneAddInstantBill}
            defaultValues={defaultValues}
            isRecurringPaymentsEnabled={isRecurringPaymentsEnabled && isPlatformJustPayV2RecurringEnabled}
            isInternationalSupportedForRecurringFlow={isInternationalSupportedForRecurringFlow}
          />
        }
      />
      <Route
        path="recurring"
        element={
          <RecurringPaymentFlowActivity
            onDone={onFlowDone}
            onClose={onClose}
            onBack={onClose}
            billFormFields={addBillFormState as RecurringNumOfOccurrencesFields | RecurringEndDateFields}
            flowUuid={flowUuid}
          />
        }
      />
    </Routes>
  );
};
